/**
 * PURPOSE: Grilla de captura de carreras (`CPT`: pista, vallas, obstáculos) y marcha (`MAR`).
 * RESPONSIBILITIES: Carril (salvo `laneMode: "none"`), orden, atleta, universidad, marca con ayuda de
 *   tiempo, estado (OK, DNS, DNF, DQ), lugar en la serie y "q" de clasificada a la final.
 *
 *   TABLERO DE CARRILES (`laneMode: "lane"`: 100, 200, 400, vallas). Una fila por carril (al menos 8),
 *   ocupada o libre. Cada atleta se mueve con el selector de carril o arrastrando su número a otra fila;
 *   si el carril destino está ocupado, los dos se intercambian. "—" deja al atleta en la serie sin
 *   carril (sección "Sin carril"). Un carril libre se llena con un atleta de la serie sin carril (en el
 *   borrador) o con un inscrito sin serie (se agrega en el servidor directo a ese carril)
 *   o buscando a un acreditado ("Buscar deportista acreditado…" abre la ventana Agregar con ese carril). Los cambios
 *   de carril viajan con "Guardar" como cualquier otra celda.
 *
 *   Lugar y "q" son del servidor (`placeInRound`, `qualified`): se ven actualizados tras guardar. La "q" va
 *   junto a la marca. Columnas tras la marca: Puntos WA (combinadas), Lugar y Estado al final.
 * COLLABORATORS: RoundEditor.jsx, gridCells.jsx, utils/athleticsMarks.js, utils/athleticsRoundDraft.js.
 * ROUTE: winscore/src/area/AtletismoResultadosArea/components/grids/TrackGrid.jsx
 */
import React, { useState } from "react";
import { FiMove } from "react-icons/fi";

import { TRACK_STATUS_CODES, normalizeTimeMark } from "../../../../utils/athleticsMarks.js";
import {
  assignLaneInDraft,
  buildLaneBoard,
  isServerInvalidCell,
  resolveLaneCount,
  trackMarkPatch,
  trackStatusPatch,
} from "../../../../utils/athleticsRoundDraft.js";
import { ENTRY_STATUS_TITLES, institutionAbrev, participantName } from "../../labels.js";
import { Badge, compactSelectClass } from "../ui.jsx";
import {
  CellInput,
  FrozenScroll,
  InstitutionCell,
  NumberCell,
  ParticipantCell,
  PlaceCell,
  RowActions,
  buildFrozenColumns,
  rowToneClass,
  tdClass,
  thCenterClass,
  thClass,
} from "./gridCells.jsx";

export const TRACK_MARK_HELP =
  "Tiempo: 10.45 · 1:58.32 · 35:12.40 (la coma vale como punto). También 1045 → 10.45. Escribe DNS, DNF o DQ para marcar el estado.";

const LANE_MOVE_HELP = "Mover a otro carril: si está ocupado, los dos atletas se intercambian. — = sin carril.";

export const TrackStatusSelect = ({ value, onChange, disabled, ariaLabel }) => (
  <select
    value={value ?? ""}
    onChange={(event) => onChange(event.target.value)}
    disabled={disabled}
    aria-label={ariaLabel}
    className={`${compactSelectClass} w-20`}
  >
    <option value="">—</option>
    <option value="OK" title={ENTRY_STATUS_TITLES.OK}>
      OK
    </option>
    {TRACK_STATUS_CODES.map((code) => (
      <option key={code} value={code} title={ENTRY_STATUS_TITLES[code]}>
        {code}
      </option>
    ))}
  </select>
);

const shortName = (participant) => {
  const name = participantName(participant);
  return name.length > 22 ? `${name.slice(0, 21)}…` : name;
};

/** Selector de carril de un atleta del tablero: su carril, los libres y los ocupados (intercambio). */
const LaneSelect = ({ lane, lanes, onChange, disabled, ariaLabel }) => (
  <select
    value={lane ? String(lane) : ""}
    onChange={(event) => onChange(event.target.value)}
    disabled={disabled}
    aria-label={ariaLabel}
    title={LANE_MOVE_HELP}
    className={`${compactSelectClass} w-14 px-1 text-center font-black`}
  >
    <option value="">—</option>
    {lanes.map((slot) => (
      <option key={slot.lane} value={String(slot.lane)}>
        {slot.lane === lane
          ? slot.lane
          : slot.entry
          ? `${slot.lane} ⇄ ${shortName(slot.entry.participant)}`
          : `${slot.lane} · libre`}
      </option>
    ))}
  </select>
);

const TrackGrid = ({
  prueba,
  round,
  draft,
  setRow,
  setDraft,
  readOnly,
  localInvalidKeys,
  serverInvalid,
  changedIds,
  canRemove,
  rowActions,
  showWaPoints,
  nav,
  unassigned = [],
  onAddEntry,
  onSearchAtLane,
  poolBlocked = false,
}) => {
  const entries = Array.isArray(round?.entries) ? round.entries : [];
  const showLane = prueba?.laneMode !== "none";
  const laneBoard = prueba?.laneMode === "lane" && typeof setDraft === "function";
  const showQualified = Boolean(prueba?.hasFinal) && round?.type === "heat";
  const [dragOverLane, setDragOverLane] = useState(null);
  const frozen = buildFrozenColumns([
    showLane ? ["lane", laneBoard ? 5.5 : 4] : null,
    ["order", 4],
    ["athlete", 12],
    ["institution", 5.5],
  ]);
  // Columnas a la derecha de las fijas: marca, estado, lugar y las opcionales.
  const trailingColumns = 3 + (showWaPoints ? 1 : 0) + (canRemove ? 1 : 0);

  const board = laneBoard ? buildLaneBoard(entries, draft, resolveLaneCount(prueba, draft)) : null;
  const pool = laneBoard && typeof onAddEntry === "function" ? (Array.isArray(unassigned) ? unassigned : []) : [];

  const moveToLane = (rowId, lane) => setDraft((current) => assignLaneInDraft(current, rowId, lane));

  const fillLane = (lane, value) => {
    if (value === "search") onSearchAtLane(lane);
    else if (value.startsWith("row:")) moveToLane(Number(value.slice(4)), lane);
    else if (value.startsWith("pool:")) {
      const item = pool.find((candidate) => String(candidate.entryId) === value.slice(5));
      if (item) onAddEntry(item, lane);
    }
  };

  const dropTarget = (lane) =>
    readOnly || !board
      ? {}
      : {
          onDragOver: (event) => {
            event.preventDefault();
            event.dataTransfer.dropEffect = "move";
            if (dragOverLane !== lane) setDragOverLane(lane);
          },
          onDragLeave: () => setDragOverLane((current) => (current === lane ? null : current)),
          onDrop: (event) => {
            event.preventDefault();
            setDragOverLane(null);
            const rowId = Number(event.dataTransfer.getData("text/plain"));
            if (rowId) moveToLane(rowId, lane);
          },
        };

  const renderLaneCell = (entry, row) => {
    const name = entry.participant?.name || "atleta";
    if (!laneBoard) {
      return (
        <NumberCell
          value={row.lane}
          onChange={(value) => setRow(entry.id, { lane: value })}
          disabled={readOnly}
          ariaLabel={`Carril de ${name}`}
        />
      );
    }
    const lane = Number(row.lane) || null;
    return (
      <div className="flex items-center justify-center gap-1">
        {!readOnly ? (
          <span
            draggable
            onDragStart={(event) => {
              event.dataTransfer.setData("text/plain", String(entry.id));
              event.dataTransfer.effectAllowed = "move";
            }}
            onDragEnd={() => setDragOverLane(null)}
            className="cursor-grab rounded p-0.5 text-slate-400 hover:bg-slate-100 hover:text-slate-600 active:cursor-grabbing"
            title="Arrastra a otro carril"
            aria-hidden="true"
          >
            <FiMove />
          </span>
        ) : null}
        <LaneSelect
          lane={lane}
          lanes={board.lanes}
          onChange={(value) => moveToLane(entry.id, value)}
          disabled={readOnly}
          ariaLabel={`Carril de ${name}`}
        />
      </div>
    );
  };

  const renderEntryRow = (entry, rowIndex, { lane = null } = {}) => {
    const row = draft.rows[entry.id];
    if (!row) return null;
    const name = entry.participant?.name || "atleta";
    const tone =
      lane !== null && dragOverLane === lane
        ? "bg-indigo-50"
        : rowToneClass({ serverInvalid: serverInvalid?.rowId === entry.id, changed: changedIds.has(entry.id) });
    return (
      <tr key={entry.id} className={`border-b border-slate-100 last:border-b-0 ${tone}`} {...(lane !== null ? dropTarget(lane) : {})}>
        {showLane ? <td {...frozen("lane", `${tdClass} text-center`)}>{renderLaneCell(entry, row)}</td> : null}
        <td {...frozen("order", `${tdClass} text-center`)}>
          <NumberCell
            value={row.order}
            onChange={(value) => setRow(entry.id, { order: value })}
            disabled={readOnly}
            ariaLabel={`Orden de ${name}`}
          />
        </td>
        <td {...frozen("athlete", tdClass)}>
          <ParticipantCell participant={entry.participant} />
        </td>
        <td {...frozen("institution", tdClass)}>
          <InstitutionCell institution={entry.participant?.institution} />
        </td>
        <td className={`${tdClass} text-center`}>
          <CellInput
            value={row.mark}
            onChange={(raw) => setRow(entry.id, (current) => trackMarkPatch(current, raw))}
            onBlur={() => setRow(entry.id, (current) => ({ mark: normalizeTimeMark(current.mark) }))}
            disabled={readOnly}
            invalid={isServerInvalidCell(serverInvalid, {
              rowId: entry.id,
              kind: "mark",
              cellValue: row.mark,
            })}
            softInvalid={localInvalidKeys.has(`${entry.id}|mark`)}
            inputRef={nav.register(rowIndex, "mark")}
            onKeyDown={nav.onKeyDown(rowIndex, "mark")}
            placeholder="0.00"
            title={TRACK_MARK_HELP}
            ariaLabel={`Marca de ${name}`}
          />
          {showQualified && entry.qualified ? (
            <span className="ml-1 align-middle">
              <Badge tone="green" title="Clasificado a la final">
                {entry.qualified}
              </Badge>
            </span>
          ) : null}
        </td>
        {showWaPoints ? (
          <td className={`${tdClass} text-center text-sm font-bold text-indigo-700`}>{entry.waPoints ?? "—"}</td>
        ) : null}
        <td className={`${tdClass} text-center`}>
          <PlaceCell place={entry.placeInRound} />
        </td>
        <td className={`${tdClass} text-center`}>
          <TrackStatusSelect
            value={row.status}
            onChange={(value) => setRow(entry.id, trackStatusPatch(value))}
            disabled={readOnly}
            ariaLabel={`Estado de ${name}`}
          />
        </td>
        {canRemove ? (
          <td className={`${tdClass} text-center`}>
            <RowActions name={name} {...rowActions(entry)} />
          </td>
        ) : null}
      </tr>
    );
  };

  const renderFreeLane = (lane) => {
    const canSearch = typeof onSearchAtLane === "function";
    const options = board.unplaced.length + pool.length + (canSearch ? 1 : 0);
    return (
      <tr
        key={`lane-${lane}`}
        className={`border-b border-slate-100 last:border-b-0 ${dragOverLane === lane ? "bg-indigo-50" : "bg-white"}`}
        {...dropTarget(lane)}
      >
        <td {...frozen("lane", `${tdClass} text-center`)}>
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full border border-dashed border-slate-300 text-sm font-black text-slate-400">
            {lane}
          </span>
        </td>
        <td {...frozen("order", tdClass)} />
        <td {...frozen("athlete", tdClass)}>
          {readOnly || !options ? (
            <span className="text-xs italic text-slate-400">Carril libre</span>
          ) : (
            <select
              value=""
              onChange={(event) => fillLane(lane, event.target.value)}
              aria-label={`Agregar un participante al carril ${lane}`}
              className={`${compactSelectClass} w-full text-xs`}
            >
              <option value="">+ Carril libre: agregar…</option>
              {board.unplaced.length ? (
                <optgroup label="En esta serie, sin carril">
                  {board.unplaced.map((entry) => (
                    <option key={entry.id} value={`row:${entry.id}`}>
                      {participantName(entry.participant)} · {institutionAbrev(entry.participant?.institution)}
                    </option>
                  ))}
                </optgroup>
              ) : null}
              {pool.length ? (
                <optgroup label={poolBlocked ? "Inscritos sin serie (guarda los cambios primero)" : "Inscritos sin serie"}>
                  {pool.map((item) => (
                    <option key={item.entryId} value={`pool:${item.entryId}`} disabled={poolBlocked}>
                      {item.name || "Sin nombre"} · {institutionAbrev(item.institution)}
                    </option>
                  ))}
                </optgroup>
              ) : null}
              {canSearch ? (
                <option value="search" disabled={poolBlocked}>
                  {poolBlocked ? "Buscar deportista acreditado… (guarda los cambios primero)" : "Buscar deportista acreditado…"}
                </option>
              ) : null}
            </select>
          )}
        </td>
        <td {...frozen("institution", tdClass)} />
        <td colSpan={trailingColumns} className={tdClass} />
      </tr>
    );
  };

  const header = (
    <thead>
      <tr className="border-b border-slate-200 bg-slate-50">
        {showLane ? <th {...frozen("lane", thCenterClass)}>Carril</th> : null}
        <th {...frozen("order", thCenterClass)}>Orden</th>
        <th {...frozen("athlete", thClass)}>Atleta</th>
        <th {...frozen("institution", thClass)} title="Universidad">
          Univ.
        </th>
        <th className={thCenterClass} title={showQualified ? "Marca · q = clasificado a la final" : undefined}>
          {showQualified ? "Marca · q" : "Marca"}
        </th>
        {showWaPoints ? <th className={thCenterClass}>Puntos WA</th> : null}
        <th className={thCenterClass}>Lugar</th>
        <th className={thCenterClass}>Estado</th>
        {canRemove ? <th className={thCenterClass} aria-label="Acciones" /> : null}
      </tr>
    </thead>
  );

  if (!board) {
    return (
      <FrozenScroll>
        <table className="w-full min-w-[720px] table-auto">
          {header}
          <tbody>{entries.map((entry, index) => renderEntryRow(entry, index))}</tbody>
        </table>
      </FrozenScroll>
    );
  }

  // Índice visual para la navegación con Enter/↓: carriles ocupados en orden y luego los sin carril.
  let visualIndex = 0;
  return (
    <FrozenScroll>
      <table className="w-full min-w-[720px] table-auto">
        {header}
        <tbody>
          {board.lanes.map(({ lane, entry }) => {
            if (!entry) return renderFreeLane(lane);
            const row = renderEntryRow(entry, visualIndex, { lane });
            visualIndex += 1;
            return row;
          })}
          {board.unplaced.length ? (
            <tr className="border-b border-slate-200 bg-amber-50">
              <td colSpan={(showLane ? 1 : 0) + 3 + trailingColumns} className="px-3 py-1.5 text-[11px] font-black uppercase tracking-wide text-amber-700">
                Sin carril ({board.unplaced.length}): elige su carril o arrástralo a uno libre
              </td>
            </tr>
          ) : null}
          {board.unplaced.map((entry) => {
            const row = renderEntryRow(entry, visualIndex);
            visualIndex += 1;
            return row;
          })}
        </tbody>
      </table>
    </FrozenScroll>
  );
};

export default TrackGrid;
